import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ActionIcon, Group, Input, Stack, Table, Tooltip } from "@mantine/core";
import {
  AnchorLink,
  ButtonLink,
  LoadingSuspense,
  TimeAgo,
} from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo, useState } from "react";

import { api } from "@/api.ts";
import Area from "@/components/Area.tsx";
import DownloadStatusIcon from "@/components/DownloadStatusIcon";
import { useCatalogItems } from "@/hooks/useCatalogItems.ts";
import useDownload from "@/hooks/useDownload.ts";
import { useFormatItems } from "@/hooks/useFormatItems.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/downloads/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Downloads`,
      },
    ],
  }),
});

const Downloads: FC<{ search: string }> = ({ search }) => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(() => {
      let q = api
        .from("download_project_details")
        .select(
          "id,queued_by,queued_at,datasets,area,format,status,project_id,project_identifier,project_name",
        )
        .order("queued_at", { ascending: false });
      if (search) {
        q = q.or(
          `project_identifier.ilike.%${search}%,project_name.ilike.%${search}%`,
        );
      }
      return q;
    }, [search]),
  );
  const catalog = useCatalogItems();

  const formatItems = useFormatItems();
  const download = useDownload();

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Project</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Datasets</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Requested by</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Requested at</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Format</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Area</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Status</Trans>
          </Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.map((d) => (
          <Table.Tr key={d.id}>
            <Table.Td>
              <AnchorLink
                to="/projects/$identifier"
                params={{ identifier: d.project_identifier }}
                underline="never"
                fz="inherit"
              >
                {d.project_identifier} {d.project_name}
              </AnchorLink>
            </Table.Td>
            <Table.Td>
              {d.datasets
                .map((uri) => catalog.forUri(uri)?.properties.title)
                .join(", ")}
            </Table.Td>
            <Table.Td>{d.queued_by}</Table.Td>
            <Table.Td>
              <TimeAgo date={d.queued_at} />
            </Table.Td>
            <Table.Td>
              <Tooltip
                label={formatItems[d.format]?.description}
                withArrow
                multiline
                w={220}
                position="left-start"
              >
                <span>{formatItems[d.format]?.title}</span>
              </Tooltip>
            </Table.Td>
            <Table.Td>
              <Area value={d.area} />
            </Table.Td>
            <Table.Td>
              <DownloadStatusIcon status={d.status} />
            </Table.Td>
            <Table.Td>
              <ActionIcon.Group>
                <ActionIcon
                  onClick={() => download.download(d)}
                  disabled={d.status !== "completed" || !!download.loading}
                  loading={download.loading === d.id}
                  color="green"
                  variant="light"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </ActionIcon>
              </ActionIcon.Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

function RouteComponent() {
  const [search, setSearch] = useState("");

  const { t } = useLingui();

  return (
    <Stack gap="lg">
      <Group>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t`Search projects...`}
        />
        <ButtonLink to="/flows/download/choose-project" color="green">
          <Trans>Start new</Trans>
        </ButtonLink>
      </Group>
      <LoadingSuspense>
        <Downloads search={search} />
      </LoadingSuspense>
    </Stack>
  );
}
